import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../../services/api.ts";
import Navbar from "../navbar/Navbar";
import "./ActivityLog.css";

const ActivityLog = () => {
    const navigate = useNavigate();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [checkingAdmin, setCheckingAdmin] = useState(true);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(50);

    // Filters
    const [filters, setFilters] = useState({
        username: "",
        action: "",
        target_type: "",
        date_from: "",
        date_to: "",
        search: "",
    });

    // Check admin status
    useEffect(() => {
        const checkAdmin = async () => {
            try {
                const response = await API.get("/accounts/users/me/");
                if (response.data.is_staff) {
                    setIsAdmin(true);
                } else {
                    navigate("/Dashboard");
                }
            } catch (err) {
                navigate("/Dashboard");
            } finally {
                setCheckingAdmin(false);
            }
        };
        checkAdmin();
    }, [navigate]);

    const fetchActivities = useCallback(async () => {
        if (!isAdmin) return;
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.set("page", page);
            if (filters.username) params.set("username", filters.username);
            if (filters.action) params.set("action", filters.action);
            if (filters.target_type) params.set("target_type", filters.target_type);
            if (filters.date_from) params.set("date_from", filters.date_from);
            if (filters.date_to) params.set("date_to", filters.date_to);
            if (filters.search) params.set("search", filters.search);

            const response = await API.get(`/activity-log/?${params.toString()}`);
            setActivities(response.data.results || []);
            setTotalCount(response.data.count || 0);
        } catch (err) {
            console.error("Error fetching activity log:", err);
            if (err.response?.status === 403) {
                toast.error("Access denied. Admin privileges required.");
                navigate("/Dashboard");
            } else {
                toast.error("Failed to fetch activity log.");
            }
        } finally {
            setLoading(false);
        }
    }, [isAdmin, page, filters, navigate]);

    const fetchStats = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const response = await API.get("/activity-log/stats/");
            setStats(response.data);
        } catch (err) {
            console.error("Error fetching stats:", err);
        }
    }, [isAdmin]);

    useEffect(() => {
        if (isAdmin) {
            fetchActivities();
        }
    }, [isAdmin, fetchActivities]);

    useEffect(() => {
        if (isAdmin) {
            fetchStats();
        }
    }, [isAdmin, fetchStats]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const handleClearFilters = () => {
        setFilters({
            username: "",
            action: "",
            target_type: "",
            date_from: "",
            date_to: "",
            search: "",
        });
        setPage(1);
    };

    const getTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const seconds = Math.floor((now - date) / 1000);
        if (seconds < 60) return "just now";
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    const getActionIcon = (action) => {
        const icons = {
            logged_in: "fa-sign-in-alt",
            registered: "fa-user-plus",
            created: "fa-plus-circle",
            updated: "fa-edit",
            deleted: "fa-trash",
            uploaded: "fa-upload",
            executed: "fa-play-circle",
            launched: "fa-external-link-alt",
            invited: "fa-user-plus",
            joined: "fa-user-check",
            assigned: "fa-tasks",
            unassigned: "fa-user-times",
            left: "fa-user-minus",
            status_changed: "fa-exchange-alt",
        };
        return icons[action] || "fa-circle";
    };

    const getTargetIcon = (targetType) => {
        const icons = {
            study: "fa-book",
            dataset: "fa-database",
            model: "fa-cube",
            viewer: "fa-eye",
            session: "fa-user",
            assignment: "fa-tasks",
            membership: "fa-users",
        };
        return icons[targetType] || "fa-file";
    };

    const getUserInitials = (username) => {
        if (!username) return "?";
        return username.substring(0, 2).toUpperCase();
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    const actionChoices = [
        "logged_in", "registered", "created", "updated", "deleted",
        "uploaded", "executed", "launched", "invited", "joined",
        "assigned", "unassigned", "left", "status_changed",
    ];

    const targetTypeChoices = [
        "study", "dataset", "model", "viewer", "session",
        "assignment", "membership",
    ];

    if (checkingAdmin) {
        return (
            <div className="activity-log-page">
                <Navbar />
                <div className="activity-log-container">
                    <div className="activity-log-loading">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="activity-log-page">
            <Navbar />
            <div className="activity-log-container">
                <div className="activity-log-header">
                    <h2><i className="fa fa-history"></i> Activity Log</h2>
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => { fetchActivities(); fetchStats(); }}
                    >
                        <i className="fa fa-refresh me-1"></i> Refresh
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="activity-log-stats">
                        <div className="stat-card">
                            <div className="stat-icon total"><i className="fa fa-list"></i></div>
                            <div className="stat-info">
                                <div className="stat-value">{stats.total}</div>
                                <div className="stat-label">Total Events</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon logins"><i className="fa fa-sign-in-alt"></i></div>
                            <div className="stat-info">
                                <div className="stat-value">{stats.by_action?.logged_in || 0}</div>
                                <div className="stat-label">Logins</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon models"><i className="fa fa-play-circle"></i></div>
                            <div className="stat-info">
                                <div className="stat-value">{stats.by_action?.executed || 0}</div>
                                <div className="stat-label">Model Runs</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon viewers"><i className="fa fa-eye"></i></div>
                            <div className="stat-info">
                                <div className="stat-value">{stats.by_action?.launched || 0}</div>
                                <div className="stat-label">Viewer Opens</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="activity-log-filters">
                    <div className="filter-group">
                        <label>User</label>
                        <input
                            type="text"
                            placeholder="Username..."
                            value={filters.username}
                            onChange={(e) => handleFilterChange("username", e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <label>Action</label>
                        <select
                            value={filters.action}
                            onChange={(e) => handleFilterChange("action", e.target.value)}
                        >
                            <option value="">All actions</option>
                            {actionChoices.map(a => (
                                <option key={a} value={a}>{a.replace("_", " ")}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Target</label>
                        <select
                            value={filters.target_type}
                            onChange={(e) => handleFilterChange("target_type", e.target.value)}
                        >
                            <option value="">All types</option>
                            {targetTypeChoices.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>From</label>
                        <input
                            type="date"
                            value={filters.date_from}
                            onChange={(e) => handleFilterChange("date_from", e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <label>To</label>
                        <input
                            type="date"
                            value={filters.date_to}
                            onChange={(e) => handleFilterChange("date_to", e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <label>Search</label>
                        <input
                            type="text"
                            placeholder="Target name..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange("search", e.target.value)}
                        />
                    </div>
                    <div className="filter-actions">
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleClearFilters}
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {/* Activity Table */}
                <div className="activity-log-table-wrapper">
                    {loading ? (
                        <div className="activity-log-loading">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : activities.length === 0 ? (
                        <div className="activity-log-empty">
                            <i className="fa fa-inbox"></i>
                            <p>No activity found</p>
                        </div>
                    ) : (
                        <>
                            <table className="activity-log-table">
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>User</th>
                                        <th>Action</th>
                                        <th>Target</th>
                                        <th>Name</th>
                                        <th>Project</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activities.map((activity) => (
                                        <tr key={activity.id}>
                                            <td className="time-cell" title={new Date(activity.created_at).toLocaleString()}>
                                                {getTimeAgo(activity.created_at)}
                                            </td>
                                            <td>
                                                <div className="user-cell">
                                                    <div className="user-avatar-small">
                                                        {getUserInitials(activity.user?.username)}
                                                    </div>
                                                    <span className="user-name">{activity.user?.username || "System"}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`action-badge ${activity.action}`}>
                                                    <i className={`fa ${getActionIcon(activity.action)}`}></i>
                                                    {activity.action?.replace("_", " ")}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="target-badge">
                                                    <i className={`fa ${getTargetIcon(activity.target_type)}`}></i>
                                                    {activity.target_type}
                                                </span>
                                            </td>
                                            <td>{activity.target_name || "-"}</td>
                                            <td>{activity.project_name || "-"}</td>
                                            <td className="details-cell" title={activity.description}>
                                                {activity.description || "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            <div className="activity-log-pagination">
                                <div className="pagination-info">
                                    Showing {Math.min((page - 1) * pageSize + 1, totalCount)}-{Math.min(page * pageSize, totalCount)} of {totalCount} events
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        disabled={page <= 1}
                                        onClick={() => setPage(1)}
                                        title="First page"
                                    >
                                        <i className="fa fa-angle-double-left"></i>
                                    </button>
                                    <button
                                        disabled={page <= 1}
                                        onClick={() => setPage(p => p - 1)}
                                    >
                                        <i className="fa fa-angle-left"></i>
                                    </button>
                                    <button className="active" disabled>
                                        {page} / {totalPages || 1}
                                    </button>
                                    <button
                                        disabled={page >= totalPages}
                                        onClick={() => setPage(p => p + 1)}
                                    >
                                        <i className="fa fa-angle-right"></i>
                                    </button>
                                    <button
                                        disabled={page >= totalPages}
                                        onClick={() => setPage(totalPages)}
                                        title="Last page"
                                    >
                                        <i className="fa fa-angle-double-right"></i>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActivityLog;
